import {
  type ChangeEvent,
  useEffect,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { defineMessage, useIntl } from 'react-intl';

import { Checkbox } from '../../../../forms/checkbox/Checkbox.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { DataTableV2DefaultCell } from '../../components/DataTableV2DefaultCell.js';
import { type Row, Table } from '../../hooks/useTable/types.js';

const selectAllTooltip = defineMessage({
  defaultMessage:
    '{nextSelection, select, select {Select} deselect {Deselect} other{}} all rows {scope, select, page {on current page} all {in the table} other {}}',
  id: 'KxAo/sbsalGLDonj',
  description: 'Toggle selection for all rows on the page',
});

const rowCheckboxMessage = defineMessage({
  defaultMessage: 'Toggle row selection for row with id "{id}".',
  id: 'FJE1QgzcKO8lKmt4',
  description: 'Toggle selection for an individual row',
});

/**
 * DataTableV2 row selection header.
 * @internal
 */
export function RowSelectionHeader(props: {
  table: Table<unknown>;
  resetBatchSelection: (ev: MouseEvent) => void;
}) {
  const { table, resetBatchSelection } = props;
  const value = getCheckboxValue(table);
  const isDisabled = isSelectAllDisabled(table);

  const intl = useIntl();
  const checkboxMessage = intl.formatMessage(selectAllTooltip, {
    nextSelection:
      value === false || value === 'indeterminate' ? 'select' : 'deselect',
    scope: table.options.rowSelectionSelectAllMode,
  });

  useEffect(() => {
    document.addEventListener('click', resetBatchSelection);
    return () => {
      document.removeEventListener('click', resetBatchSelection);
    };
  }, [resetBatchSelection]);

  function toggleSelectAll(event: ChangeEvent<HTMLInputElement> | undefined) {
    if (isDisabled) {
      event?.preventDefault();
      return;
    }
    if (table.options.rowSelectionSelectAllMode === 'page') {
      if (table.options.rowSelectionLimit !== undefined) {
        table.getToggleAllLimitedPageRowsSelectedHandler()(event);
      } else {
        table.getToggleAllPageRowsSelectedHandler()(event);
      }
    } else if (table.options.rowSelectionSelectAllMode === 'all') {
      if (table.options.rowSelectionLimit !== undefined) {
        table.getToggleAllLimitedRowsSelectedHandler()(event);
      } else {
        table.getToggleAllRowsSelectedHandler()(event);
      }
    }
  }

  const checkbox = (
    <Checkbox
      value={isDisabled ? false : value}
      aria-disabled={isDisabled}
      aria-label={checkboxMessage}
      onChange={(_, event) => toggleSelectAll(event)}
    />
  );

  return (
    <DataTableV2DefaultCell>
      {isDisabled ? (
        checkbox
      ) : (
        <Tooltip text={checkboxMessage}>{checkbox}</Tooltip>
      )}
    </DataTableV2DefaultCell>
  );
}

/**
 * @returns true, if the 'select all' checkbox shall be disabled, false otherwise.
 */
function isSelectAllDisabled(table: Table<unknown>) {
  const isTableEmpty = table.getRowCount() === 0;
  if (isTableEmpty) {
    return true;
  }
  let isAllRowsSelectionDisabled = false;
  if (table.options.rowSelectionSelectAllMode === 'page') {
    isAllRowsSelectionDisabled = table.getIsAllPageRowsSelectionDisabled();
  } else if (table.options.rowSelectionSelectAllMode === 'all') {
    isAllRowsSelectionDisabled = table.getIsAllRowsSelectionDisabled();
  }
  return isAllRowsSelectionDisabled;
}

/**
 * @returns the current state of the 'select all' checkbox
 */
function getCheckboxValue(table: Table<unknown>): boolean | 'indeterminate' {
  if (table.options.rowSelectionSelectAllMode === 'page') {
    return table.getIsSomePageRowsSelected()
      ? 'indeterminate'
      : table.getIsAllPageRowsSelected();
  }
  if (table.options.rowSelectionSelectAllMode === 'all') {
    return table.getIsSomeRowsSelected()
      ? 'indeterminate'
      : table.getIsAllRowsSelected();
  }
  return false;
}

const resolveCheckboxValue = (
  row: Row<unknown>,
  table: Table<unknown>,
): boolean | 'indeterminate' => {
  if (row.subRows.length === 0) {
    return row.getIsSelected();
  }

  // we need to compute the correct checked state for parent rows as we don't store 'intermediate'
  return row.getIsSomeSelected()
    ? 'indeterminate'
    : row.getIsAllSubRowsSelected();
};

/**
 * DataTableV2 row selection cell.
 * @internal
 */
export function RowSelectionCell(props: {
  row: Row<unknown>;
  table: Table<unknown>;
}) {
  const { row, table } = props;
  const intl = useIntl();

  const isDisabled = row.getShouldDisableRowSelection();
  return (
    <DataTableV2DefaultCell
      onMouseDown={(event: ReactMouseEvent) => {
        document.getSelection()?.removeAllRanges();
        event.preventDefault();
      }}
    >
      <Checkbox
        aria-label={intl.formatMessage(rowCheckboxMessage, {
          id: row.id,
        })}
        value={resolveCheckboxValue(row, table)}
        aria-disabled={isDisabled}
        onChange={(value, event) => {
          if (isDisabled) {
            event?.preventDefault();
            return;
          }
          row.batchSelect(value, (event?.nativeEvent as MouseEvent).shiftKey);
        }}
        data-row-selection-checkbox
      />
    </DataTableV2DefaultCell>
  );
}
